import { useEffect, useRef } from "react";
import { useLocale } from "src/locales/useLocale";

interface RelativeTimeProps {
	timestamp: Date;
	className?: string;
	as?: React.ElementType;
}

export function RelativeTime({
	timestamp,
	className,
	as: Component = "span",
}: RelativeTimeProps) {
	const elementRef = useRef<HTMLElement>(null);
	const intervalIdRef = useRef<NodeJS.Timeout>();
	const currentIntervalRef = useRef<number>(0);
	const { formatRelative } = useLocale();

	useEffect(() => {
		const getInterval = (): number => {
			const secondsElapsed = Math.floor(
				(Date.now() - timestamp.getTime()) / 1000,
			);

			if (secondsElapsed >= 86400) return 3600000; // 1 hour
			if (secondsElapsed >= 3600) return 300000; // 5 minutes
			if (secondsElapsed >= 60) return 60000; // 1 minute
			return 1000; // 1 second
		};

		const updateTime = () => {
			if (elementRef.current) {
				elementRef.current.textContent = formatRelative(timestamp);
			}

			const newInterval = getInterval();

			if (newInterval !== currentIntervalRef.current) {
				if (intervalIdRef.current) {
					clearInterval(intervalIdRef.current);
				}
				intervalIdRef.current = setInterval(updateTime, newInterval);
				currentIntervalRef.current = newInterval;
			}
		};

		updateTime();

		return () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
				currentIntervalRef.current = 0;
			}
		};
	}, [timestamp, formatRelative]);

	return (
		<Component ref={elementRef} className={className}>
			{formatRelative(timestamp)}
		</Component>
	);
}
